/**
 * Analytics Accordion Components
 * Wrappers for Accordion components with automatic event tracking
 */

import * as React from "react"
import {
  Accordion as BaseAccordion,
  AccordionItem as BaseAccordionItem,
  AccordionTrigger as BaseAccordionTrigger,
  AccordionContent,
} from "@/shared/widgets/accordion"
import * as AccordionPrimitive from "@radix-ui/react-accordion"
import { useAnalytics } from "../hooks/useAnalytics"
import type { AnalyticsEventProviderType } from "../../application/services/AnalyticsService"

// Re-export non-interactive components as-is
export { AccordionContent }

// Re-export AccordionItem as-is (no tracking needed, just a container)
export const AccordionItem = BaseAccordionItem

// Wrapper for Accordion root
// Use the actual props type from Radix UI
export type AnalyticsAccordionProps = 
  | (AccordionPrimitive.AccordionSingleProps & {
      /**
       * Event name to track when accordion value changes
       * Will include the new value in event properties
       */
      changeEventName?: string
      /**
       * Additional properties to include with events
       */
      eventProperties?: Record<string, unknown>
      /**
       * Which analytics event providers should receive this event
       * If not specified, event is sent to all enabled providers
       */
      eventProviders?: AnalyticsEventProviderType[]
    })
  | (AccordionPrimitive.AccordionMultipleProps & {
      /**
       * Event name to track when accordion value changes
       * Will include the new value in event properties
       */
      changeEventName?: string
      /**
       * Additional properties to include with events
       */
      eventProperties?: Record<string, unknown>
      /**
       * Which analytics event providers should receive this event
       * If not specified, event is sent to all enabled providers
       */
      eventProviders?: AnalyticsEventProviderType[]
    })

export const Accordion = React.forwardRef<
  React.ComponentRef<typeof BaseAccordion>,
  AnalyticsAccordionProps
>(({ changeEventName, eventProperties, eventProviders, onValueChange, type, ...props }, ref) => {
  const { analytics, isInitialized } = useAnalytics()

  // Handle both single and multiple types
  const handleValueChange = (value: string | string[]) => {
    if (changeEventName && analytics && isInitialized) {
      try {
        analytics.trackEvent(changeEventName, {
          ...eventProperties,
          accordion_value: value,
          accordion_type: type || 'single',
        }, { eventProviders })
      } catch (error) {
        console.error(`Error tracking event "${changeEventName}":`, error)
      }
    }
    
    // Call original onValueChange with proper type casting
    if (onValueChange) {
      if (type === 'multiple') {
        (onValueChange as (value: string[]) => void)(value as string[])
      } else {
        (onValueChange as (value: string) => void)(value as string)
      }
    }
  }

  // Type assertion needed because BaseAccordion has union type
  // We handle both single and multiple types in handleValueChange
  return (
    <BaseAccordion
      ref={ref}
      type={type}
      onValueChange={handleValueChange as AccordionPrimitive.AccordionSingleProps['onValueChange'] & AccordionPrimitive.AccordionMultipleProps['onValueChange']}
      {...(props as Omit<AccordionPrimitive.AccordionSingleProps, 'onValueChange' | 'type'> & Omit<AccordionPrimitive.AccordionMultipleProps, 'onValueChange' | 'type'>)}
    />
  )
})
Accordion.displayName = "AnalyticsAccordion"

// Wrapper for AccordionTrigger
export interface AnalyticsAccordionTriggerProps
  extends React.ComponentPropsWithoutRef<typeof BaseAccordionTrigger> {
  /**
   * Event name to track when trigger is clicked
   */
  eventName?: string
  /**
   * Additional properties to include with the event
   */
  eventProperties?: Record<string, unknown>
  /**
   * Which analytics event providers should receive this event
   * If not specified, event is sent to all enabled providers
   */
  eventProviders?: AnalyticsEventProviderType[]
}

export const AccordionTrigger = React.forwardRef<
  React.ComponentRef<typeof BaseAccordionTrigger>,
  AnalyticsAccordionTriggerProps
>(({ eventName, eventProperties, eventProviders, onClick, children, ...props }, ref) => {
  const { analytics, isInitialized } = useAnalytics()

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (eventName && analytics && isInitialized) {
      try {
        analytics.trackEvent(eventName, {
          ...eventProperties,
          trigger_text: typeof children === 'string' ? children : 'accordion_trigger',
        }, { eventProviders })
      } catch (error) {
        console.error(`Error tracking event "${eventName}":`, error)
      }
    }
    onClick?.(e)
  }

  return (
    <BaseAccordionTrigger
      ref={ref}
      onClick={handleClick}
      {...props}
    >
      {children}
    </BaseAccordionTrigger>
  )
})
AccordionTrigger.displayName = "AnalyticsAccordionTrigger"

